/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Product Offerings Types
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.10.0
 */

import { ETextBlockStatus } from '../../components/common/TextBlockStatus/textBlockStatusTypes';
import { IAction, IActionWithPayload } from '../../modules/app/createAction';
import { EAccountType } from '../PageAuth/constants/authConstants';
import { IAuthRegion } from '../PageAuth/types/authTypes';
import { ELabelsImportField } from '../PageLabelsImport/labelsImportConstants';
import { IMegaPower } from '../PageSubscriberId/components/Info/types';
import {
    PRODUCT_OFFERING_CLEAR,
    PRODUCT_OFFERING_CREATE_INQUIRY_FAILURE,
    PRODUCT_OFFERING_CREATE_INQUIRY_START,
    PRODUCT_OFFERING_CREATE_INQUIRY_SUCCESS,
    PRODUCT_OFFERING_GET_SUBSCRIBERS_CHECK_LIST_ACTION,
    PRODUCT_OFFERING_GET_SUBSCRIBERS_CHECK_LIST_FAILURE,
    PRODUCT_OFFERING_GET_SUBSCRIBERS_CHECK_LIST_SUCCESS,
    PRODUCT_OFFERING_HANDLE_NEXT,
    PRODUCT_OFFERING_SAVE_STORAGE,
    PRODUCT_OFFERING_SET_MEGAPOWER,
    PRODUCT_OFFERING_SET_PARAMS,
    PRODUCT_OFFERING_SET_STEP_VALID,
    PRODUCT_OFFERING_START_VALIDATE,
    PRODUCT_SUBSCRIBERS_LIST_INIT_START,
} from './wizardProductOfferingsConstants';
export interface IRatePlan {
    personalOfferingId?: number; // для непубличного тарифа
    limitConnections?: number; // лимит доступных подключений
    connected?: number; // количество фактических подключений

    productOfferingId: number; // для любого тарифа
    productRangeId?: number; // для публичного тарифа с бегунком
}

export interface IService {
    personalOfferingId?: number; // для непубличной услуги
    productOfferingId: number; // для любой услуге
    limitConnections?: number; // лимит доступных подключений
    connected?: number; // количество фактических подключений
}

export interface IWizardProductOfferingsTableData {
    [ELabelsImportField.MSISDN]: string;
}

export enum EActionConflictTypes {
    ERROR = 'ERROR',
    WARNING = 'WARNING',
}

export interface IActionConflictItem {
    code: string;
    message: string;
    numberCode: number;
    type: EActionConflictTypes;
    additionalProperties?: {
        mainProductOfferingName?: string;
        orderDateTime?: string;
        productOfferingName?: string;
    };
}

export interface IActionConflicts {
    [id: number]: IActionConflictItem[];
}

export interface IProductOfferingsSubscriberActivationItem {
    account: {
        id: string;
        number: string;
    };
    accountType: EAccountType;
    balance: number;
    cost: number;
    creditLimit: number;
    id: string;
    msisdn: string;
    actionConflicts: IActionConflicts;
    status: ETextBlockStatus;
    isMegaPowersAvailable?: boolean;
}

export interface IWizardProductOfferingsStartData {
    isDisconnect: boolean;
    // подключение тарифа
    ratePlan?: IRatePlan;
    regionId: string | number;
    // подключение услуг
    services?: IService[];
    onlyMegaPowers?: boolean;
}

export interface IWizardProductOfferingsLocalStorageData {
    dateFrom?: string;
    dateTo?: string;
    isDisconnect: boolean;
    isTariff?: boolean;
    regionId: number;
    removedSubscribers?: string[];
    subsCount?: number;
    ratePlan?: IRatePlan,
    services?: IService[],
}

export interface IProductOfferingsListItem {
    id: string;
    name: string;
}

export interface IMegaPowersUpdateParams {
    toActivate?: Array<{ id: IMegaPower['id'] }>;
    toDeactivate?: Array<{ id: IMegaPower['id'] }>;
}

export interface IMegaPowersTotal {
    toActivate?: Array<{ id: IMegaPower['id']; name: IMegaPower['name'] }>;
    toDeactivate?: Array<{ id: IMegaPower['id']; name: IMegaPower['name'] }>;
}

/*
 * -------------------------------------------
 *           ACTIONS
 * -------------------------------------------
 */

export type TProductOfferingsStartValidateAction = IAction<typeof PRODUCT_OFFERING_START_VALIDATE>;

export interface IProductOfferingsSetParamsActionPayload {
    dateFrom?: string;
    dateTo?: string;
    timeFrom?: string;
    timeTo?: string;
    isDisconnect?: boolean;
    isTariff?: boolean;
    maxSubsForProductActivation?: number;
    productOfferings?: IProductOfferingsListItem[];
    totalMegaPowers?: IMegaPowersTotal;
    megaPowers?: IMegaPower[];
    initialMegaPowers?: IMegaPower[];
    // при старте визарда всегда указывается id региона абонента
    regionId?: number;
    // если у абонента доступно несколько регионов, то выбирается region на основе regionId
    region?: IAuthRegion;
    removedSubscribers?: string[];
    subsCount?: number;
    subscriberActivationList?: IProductOfferingsSubscriberActivationItem[];
    totalPrice?: number;
    ratePlan?: IRatePlan;
    services?: IService[];
}

export type TProductOfferingsSetParamsAction = IActionWithPayload<
    typeof PRODUCT_OFFERING_SET_PARAMS,
    IProductOfferingsSetParamsActionPayload
>;

export type IProductOfferingsSetMegaPowerAction = IActionWithPayload<
    typeof PRODUCT_OFFERING_SET_MEGAPOWER,
    Pick<IMegaPower, 'id'>
>;

export interface IProductOfferingsSetStepValidPayload {
    isStepValid: boolean;
    validateError: string;
}

export type TProductOfferingsSetStepValidAction = IActionWithPayload<
    typeof PRODUCT_OFFERING_SET_STEP_VALID,
    IProductOfferingsSetStepValidPayload
>;

export type TProductSubscribersListInitAction = IAction<typeof PRODUCT_SUBSCRIBERS_LIST_INIT_START>;

export type TProductOfferingsHandleNextAction = IAction<typeof PRODUCT_OFFERING_HANDLE_NEXT>;

export type TProductOfferingClearAction = IAction<typeof PRODUCT_OFFERING_CLEAR>;

export type TProductOfferingsSaveStorageAction = IAction<typeof PRODUCT_OFFERING_SAVE_STORAGE>;

export interface IProductOfferingsSubscriberAddRemoveActionPayload {
    id: string;
    value: boolean;
}

export type TProductOfferingsGetSubscribersCheckListAction = IAction<
    typeof PRODUCT_OFFERING_GET_SUBSCRIBERS_CHECK_LIST_ACTION
>;
export type TProductOfferingsGetSubscribersCheckListSuccess = IActionWithPayload<
    typeof PRODUCT_OFFERING_GET_SUBSCRIBERS_CHECK_LIST_SUCCESS,
    IProductOfferingsSubscriberActivationItem[]
>;
export type TProductOfferingsGetSubscribersCheckListFailure = IActionWithPayload<
    typeof PRODUCT_OFFERING_GET_SUBSCRIBERS_CHECK_LIST_FAILURE,
    string
>;

export type TProductOfferingsCreateInquiryAction = IAction<typeof PRODUCT_OFFERING_CREATE_INQUIRY_START>;
export type TProductOfferingsCreateInquirySuccess = IActionWithPayload<
    typeof PRODUCT_OFFERING_CREATE_INQUIRY_SUCCESS,
    number
>;
export type TProductOfferingsCreateInquiryFailure = IActionWithPayload<
    typeof PRODUCT_OFFERING_CREATE_INQUIRY_FAILURE,
    string
>;

export type TProductOfferingsActions =
    | TProductOfferingsSetStepValidAction
    | TProductSubscribersListInitAction
    | TProductOfferingsSetParamsAction
    | IProductOfferingsSetMegaPowerAction
    | TProductOfferingsHandleNextAction
    | TProductOfferingClearAction
    | TProductOfferingsSaveStorageAction
    | TProductOfferingsGetSubscribersCheckListAction
    | TProductOfferingsGetSubscribersCheckListSuccess
    | TProductOfferingsGetSubscribersCheckListFailure;
/* ... */
